import { AppNavParams } from '@euler/app/Routes';
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { ServiceRecordNavParams } from '@euler/app/flows/record/ServiceRecordFlow';
import { ProductManifestIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/ProductManifestIcon';
import { RollbackIcon } from '@euler/app/flows/record/pages/detail/components/menu-icons/RollbackIcon';
import { Center } from '@euler/components/Center';
import { MaybeText } from '@euler/components/MaybeText';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { CommonTaskStatus } from '@euler/model/enum';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { ReportTabKey } from '@euler/model/viewmodel';
import { idEquals } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { ReactNode, memo } from 'react';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { MediasIcon } from './menu-icons/MediasIcon';
import { PendingIssuesIcon } from './menu-icons/PendingIssuesIcon';
import { RecordsIcon } from './menu-icons/RecordsIcon';
import { ReportsIcon } from './menu-icons/ReportsIcon';

export const ServiceRecordMenu = memo(
  ({ showRollback }: { showRollback?: boolean }) => {
    const { rpx } = useResponsiveContext();

    const { serviceRecord, reportTarget, canRollback, rollback, refresh } =
      useServiceRecordContext();

    const nav = useAppNavigation();

    const navigation =
      useNavigation<
        StackNavigationProp<AppNavParams & ServiceRecordNavParams>
      >();

    const productManifest$ = useCreation(
      () =>
        new BehaviorSubject(
          serviceRecord.productReferrals?.map<TaskProductManifestItem>(x => ({
            id: x.id,
            author: x.author,
            product: x.product,
            qty: x.qty ?? 1,
            subject: x.subject,
            jobRefs: x.jobRefs,
            origin: x.origin!,
            confirmations: x.confirmations?.filter(
              t =>
                t.subject === x.subject && idEquals(t.productId, x.product.id),
            ),
          })) ?? [],
        ),
      [serviceRecord.productReferrals],
    );

    const { shareReportViaWeixin } = useShareReport();

    const onViewReport = usePersistFn(() => {
      navigation.navigate('Report', {
        target: reportTarget,
        preInspection:
          serviceRecord.preInspectionStatus === CommonTaskStatus.Finished,
        inspection:
          serviceRecord.inspectionStatus === CommonTaskStatus.Finished,
        construction:
          serviceRecord.constructionStatus === CommonTaskStatus.Finished,
        deliveryCheck: true,
        productManifest: {
          items$: productManifest$,
          onViewDetail: item => {
            nav.push('_productDetail', {
              id: item.product.id,
            });
          },
        },
        onShare: (tab?: ReportTabKey) => {
          void shareReportViaWeixin({
            target: reportTarget,
            orderNo: reportTarget.orderNo,
            reportNo: reportTarget.reportNo,
            licensePlateNo: reportTarget.licensePlateNo,
            tab,
            success() {
              void refresh();
            },
          });
        },
      });
    });

    const onViewTimeline = usePersistFn(() => {
      navigation.navigate('timeline', {});
    });

    const onViewPendingIssues = usePersistFn(() => {
      navigation.navigate('pendingIssues', {});
    });

    const onViewMedias = usePersistFn(() => {
      navigation.navigate('mediaAlbums', {});
    });

    const onViewProductManifest = usePersistFn(() => {
      navigation.navigate('_products', {
        items$: productManifest$,
      });
    });

    const scale = serviceRecord.productReferrals?.length ? 0.95 : 1;

    return (
      <View
        css={`
          flex-direction: row;
          justify-content: space-around;
          align-items: center;
          background-color: #fff;
          border-radius: ${rpx(10)}px;
          margin-bottom: ${rpx(15)}px;
          padding: ${rpx(15)}px;
        `}
      >
        <MenuItem
          title="工作记录"
          icon={<RecordsIcon size={rpx(44) * scale} />}
          scale={scale}
          onPress={onViewTimeline}
        />
        <MenuItem
          title="遗留问题"
          scale={scale}
          icon={<PendingIssuesIcon size={rpx(40) * scale} />}
          badge={serviceRecord.pendingIssuesV2?.length}
          onPress={onViewPendingIssues}
        />
        <MenuItem
          title="媒体图库"
          scale={scale}
          icon={<MediasIcon size={rpx(45) * scale} />}
          onPress={onViewMedias}
        />
        <MenuItem
          title="完整报告"
          scale={scale}
          icon={<ReportsIcon size={rpx(48) * scale} />}
          onPress={onViewReport}
        />
        {serviceRecord.productReferrals?.length ? (
          <MenuItem
            title="产品清单"
            scale={scale}
            badge={serviceRecord.productReferrals?.length}
            icon={<ProductManifestIcon size={rpx(38) * scale} />}
            onPress={onViewProductManifest}
          />
        ) : null}
        {showRollback && canRollback ? (
          <MenuItem
            title="维修返单"
            scale={scale}
            icon={<RollbackIcon size={rpx(44) * scale} />}
            onPress={rollback}
          />
        ) : null}
      </View>
    );
  },
);

const MenuItem = memo(
  ({
    title,
    icon,
    badge,
    style,
    scale = 1,
    onPress,
  }: {
    title: ReactNode;
    icon: ReactNode;
    badge?: number;
    scale?: number;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          justify-content: center;
          align-items: center;
        `}
        style={style}
        onPress={onPress}
      >
        <Center
          css={`
            height: ${rpx(48)}px;
            padding: 0 ${rpx(16)}px;
          `}
        >
          {icon}
          {badge ? (
            <Center
              css={`
                position: absolute;
                top: ${rpx(-4)}px;
                right: ${rpx(6)}px;
                min-width: ${rpx(20)}px;
                height: ${rpx(20)}px;
                padding: ${rpx(0)}px ${rpx(5)}px;
                border-radius: ${rpx(20)}px;
                background-color: red;
              `}
            >
              <Text
                css={`
                  font-size: ${rpx(10)}px;
                  font-weight: 600;
                  color: #fff;
                `}
              >
                {badge}
              </Text>
            </Center>
          ) : null}
        </Center>
        <MaybeText
          text={title}
          css={`
            margin-top: ${rpx(3)}px;
            font-family: ${FontFamily.NotoSans.Medium};
            font-size: ${rpx(14) * scale}px;
            color: #777;
          `}
        />
      </TouchableOpacity>
    );
  },
);
